import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent
} from '@mui/material';
import {
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Fingerprint as FingerprintIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

const BulkConsentFormsPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  
  // State management
  const [forms, setForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  
  // Enhanced download states
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadReason, setDownloadReason] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    form_type: 'all',
    client_search: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 20
  });
  
  // Security dialog
  const [securityDialogOpen, setSecurityDialogOpen] = useState(false);
  const [verifiedHashes, setVerifiedHashes] = useState(new Map());

  useEffect(() => {
    fetchForms();
  }, [filters, pagination.current_page]);

  const fetchForms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      // Build params, filtering out empty values
      const params = new URLSearchParams({
        page: pagination.current_page,
        page_size: pagination.page_size,
      });
      
      // Only add filters that have values
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.form_type && filters.form_type !== 'all') params.append('form_type', filters.form_type);
      if (filters.client_search) params.append('client_search', filters.client_search);

      const response = await fetch(`${API_BASE_URL}/consent-forms/overview/?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setForms(data.forms);
      setPagination(data.pagination);
      
      // Store hashes for verification
      const hashMap = new Map();
      data.forms.forEach(form => {
        hashMap.set(form.id, form.hash);
      });
      setVerifiedHashes(hashMap);
      
    } catch (error) {
      console.error('Error fetching forms:', error);
      setError('Failed to load consent forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      form_type: 'all',
      client_search: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleSelectForm = (formId) => {
    const newSelected = new Set(selectedForms);
    if (newSelected.has(formId)) {
      newSelected.delete(formId);
    } else {
      newSelected.add(formId);
    }
    setSelectedForms(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedForms.size === forms.length) {
      setSelectedForms(new Set());
    } else {
      setSelectedForms(new Set(forms.map(form => form.id)));
    }
  };

  // Enhanced bulk download function
  const handleBulkDownload = async () => {
    if (selectedForms.size === 0) {
      setError('Please select forms to download');
      return;
    }

    setDownloading(true);
    setError(null);
    setDownloadProgress(0);
    setDownloadStatus('Initializing download...');

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      setDownloadStatus('Preparing consent forms...');
      setDownloadProgress(25);

      // Prepare hash verification data
      const verifyHashes = {};
      selectedForms.forEach(formId => {
        if (verifiedHashes.has(formId)) {
          verifyHashes[formId] = verifiedHashes.get(formId);
        }
      });

      setDownloadStatus('Generating PDF documents...');
      setDownloadProgress(50);

      // Use your existing bulk download endpoint
      const response = await fetch(`${API_BASE_URL}/consent-forms/bulk-download/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_ids: Array.from(selectedForms),
          verify_hashes: verifyHashes,
          reason: downloadReason || 'Bulk download'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      setDownloadStatus('Packaging files...');
      setDownloadProgress(75);

      // Download the ZIP file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `consent_forms_bulk_${timestamp}.zip`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus('Download complete!');
      setDownloadProgress(100);

      // Clear selection and close dialog after successful download
      setTimeout(() => {
        setSelectedForms(new Set());
        setDownloadDialogOpen(false);
        setDownloadReason('');
        setDownloadStatus('');
        setDownloadProgress(0);
      }, 2000);
      
    } catch (error) {
      console.error('Error downloading forms:', error);
      setError(error.message || 'Failed to download consent forms');
      setDownloadStatus('');
      setDownloadProgress(0);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not signed';
    return new Date(dateString).toLocaleDateString();
  };

  const getFormTypeColor = (type) => {
    switch (type) {
      case 'tattoo': return 'primary';
      case 'piercing': return 'secondary';
      case 'client': return 'info';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4">Bulk Consent Forms Manager</Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Secure download with verification
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => setSecurityDialogOpen(true)}
            >
              Security Info
            </Button>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={() => setDownloadDialogOpen(true)}
              disabled={downloading || selectedForms.size === 0}
              sx={{ 
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              }}
            >
              Download Selected ({selectedForms.size})
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Filter Consent Forms
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Form Type</InputLabel>
                <Select
                  value={filters.form_type}
                  onChange={(e) => handleFilterChange('form_type', e.target.value)}
                  label="Form Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="tattoo">Tattoo</MenuItem>
                  <MenuItem value="piercing">Piercing</MenuItem>
                  <MenuItem value="client">Client Screening</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search Client"
                value={filters.client_search}
                onChange={(e) => handleFilterChange('client_search', e.target.value)}
                placeholder="Name or email..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results Summary */}
        {pagination.total_count > 0 && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<VerifiedIcon />}>
            Found {pagination.total_count} consent forms. Showing page {pagination.current_page} of {pagination.total_pages}.
            {selectedForms.size > 0 && ` ${selectedForms.size} selected for download.`}
          </Alert>
        )}

        {/* Forms Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : forms.length === 0 ? (
          <Alert severity="info">
            No consent forms found with the current filters.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedForms.size > 0 && selectedForms.size < forms.length}
                        checked={forms.length > 0 && selectedForms.size === forms.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Form Type</TableCell>
                    <TableCell>Date Signed</TableCell>
                    <TableCell>Images</TableCell>
                    <TableCell>Security</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forms.map((form) => (
                    <TableRow key={form.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedForms.has(form.id)}
                          onChange={() => handleSelectForm(form.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {form.client.first_name} {form.client.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {form.client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={form.form_type.charAt(0).toUpperCase() + form.form_type.slice(1)}
                          color={getFormTypeColor(form.form_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(form.date_signed)}</TableCell>
                      <TableCell>
                        {form.has_images ? (
                          <Chip label={`${form.image_count} images`} size="small" />
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            No images
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VerifiedIcon color="success" fontSize="small" />
                          <Typography variant="caption" color="success.main">
                            Verified
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={(event, page) => setPagination(prev => ({ ...prev, current_page: page }))}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Download Confirmation Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onClose={() => !downloading && setDownloadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon color="primary" />
            Download {selectedForms.size} Consent Forms
          </Box>
        </DialogTitle>
        <DialogContent>
          {!downloading ? (
            <>
              <Typography paragraph>
                You are about to download {selectedForms.size} consent forms with secure verification features.
              </Typography>
              
              <TextField
                fullWidth
                label="Reason for Download (Optional - for audit trail)"
                value={downloadReason}
                onChange={(e) => setDownloadReason(e.target.value)}
                multiline
                rows={2}
                placeholder="e.g., Client records review, regulatory inspection, legal documentation..."
                sx={{ mb: 2 }}
              />

              <Alert severity="info">
                <Typography variant="body2">
                  Files will be securely packaged with verification data and timestamps for authenticity.
                </Typography>
              </Alert>
            </>
          ) : (
            <Box>
              <Typography gutterBottom>{downloadStatus}</Typography>
              <Box sx={{ width: '100%', mt: 2 }}>
                <CircularProgress 
                  variant="determinate" 
                  value={downloadProgress} 
                  size={60}
                  sx={{ display: 'block', margin: '0 auto' }}
                />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                  {downloadProgress}%
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!downloading ? (
            <>
              <Button onClick={() => setDownloadDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleBulkDownload}
                variant="contained"
                startIcon={<LockIcon />}
              >
                Start Secure Download
              </Button>
            </>
          ) : (
            <Button disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Processing...
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Security Information Dialog */}
      <Dialog
        open={securityDialogOpen}
        onClose={() => setSecurityDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Security & Verification Features
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom color="primary">
            Professional-Grade Security for Tattoo Studios
          </Typography>
          
          <Typography paragraph>
            This download system includes enhanced security features designed for tattoo studios 
            to ensure data integrity and meet professional standards:
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FingerprintIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Document Integrity
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Cryptographic Hashing" 
                        secondary="Detects any unauthorized changes to forms"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Tamper Detection" 
                        secondary="Immediate alerts if documents are modified"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Verification Codes" 
                        secondary="Unique IDs for each form download"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={1}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ShieldIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Professional Compliance
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Audit Trail" 
                        secondary="Complete log of downloads and access"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Secure Packaging" 
                        secondary="ZIP files with verification manifest"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Access Control" 
                        secondary="Only authorized users can download"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Professional Standards:</strong> These security features ensure your consent forms 
              meet industry standards and can be verified for authenticity if needed for insurance claims, 
              legal reviews, or health inspections.
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>How Verification Works:</strong> Each downloaded form includes unique verification 
              data that can be checked to confirm the document hasn't been tampered with since creation.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BulkConsentFormsPage;